import {
  useAllPlaidAccessTokensQuery,
  usePlaidSyncPreviewLazyQuery,
  type PlaidSyncPreviewQuery,
  type PlaidSyncPreviewTransactionFragment,
} from '@transactions/graphql';
import Money from 'common/Money';
import {Copy} from 'lucide-react';
import * as React from 'react';
import {Link, useParams} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {cn} from 'src/lib/utils';
import Loading from 'src/Loading';

type PreviewPage = PlaidSyncPreviewQuery['plaidSyncPreview'];
type PreviewRow = PlaidSyncPreviewTransactionFragment & {
  stream: 'added' | 'modified';
};

type StartMode = 'stored' | 'start' | 'custom';

const badgeClass = (tone: 'green' | 'red' | 'amber' | 'blue' | 'purple' | 'slate') =>
  cn(
    'rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
    tone === 'green' &&
      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    tone === 'red' && 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    tone === 'amber' &&
      'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    tone === 'blue' &&
      'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    tone === 'purple' &&
      'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    tone === 'slate' &&
      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  );

/** The one-glance "how does this Plaid row relate to the DB" marker. */
const DbStatusBadge = ({row}: {row: PreviewRow}) => {
  if (row.dbStatus === 'synced') {
    return row.dbMatches ? (
      <span className={badgeClass('green')}>in DB</span>
    ) : (
      <span className={badgeClass('amber')}>in DB, differs</span>
    );
  }
  if (row.dbStatus === 'deleted') {
    return <span className={badgeClass('slate')}>deleted in DB</span>;
  }
  if (row.pending) {
    return <span className={badgeClass('blue')}>pending</span>;
  }
  if (!row.wouldSync) {
    return (
      <span className={badgeClass('slate')}>
        skip: {row.skipReason ?? 'filtered'}
      </span>
    );
  }
  return <span className={badgeClass('red')}>not in DB</span>;
};

// An empty currency (Plaid rows carrying only unofficial_currency_code) must
// not be dressed up as USD — Money's fallback renders the bare number, which
// is what the sync would actually store.
const formatAmount = (amount: number, currency: string) =>
  Money.toDecimalString(Money.fromServer({amount, currency})).trim();

const truncateCursor = (cursor: string) =>
  cursor.length <= 24 ? cursor : `${cursor.slice(0, 12)}…${cursor.slice(-8)}`;

const CursorChip = ({label, cursor}: {label: string; cursor: string | null}) => (
  <span className="flex items-center gap-1 text-xs">
    <span className="text-muted-foreground">{label}</span>
    {cursor ? (
      <>
        <span className="bg-muted rounded px-1.5 py-0.5 font-mono">
          {truncateCursor(cursor)}
        </span>
        <button
          type="button"
          title="Copy full cursor"
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => {
            void navigator.clipboard.writeText(cursor);
          }}
        >
          <Copy className="size-3" />
        </button>
      </>
    ) : (
      <span className="text-muted-foreground italic">none (from start)</span>
    )}
  </span>
);

const prettyJson = (json: string): string => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

const ExpandedRow = ({row}: {row: PreviewRow}) => (
  <div className="bg-muted/30 flex flex-col gap-3 p-3 text-xs">
    <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
      <span>
        <span className="text-muted-foreground">transaction_id: </span>
        <span className="font-mono">{row.transactionId}</span>
      </span>
      {row.pendingTransactionId && (
        <span>
          <span className="text-muted-foreground">pending_transaction_id: </span>
          <span className="font-mono">{row.pendingTransactionId}</span>
        </span>
      )}
      <span>
        <span className="text-muted-foreground">plaid account: </span>
        <span className="font-mono">{row.plaidAccountId}</span>
      </span>
      <span>
        <span className="text-muted-foreground">date / authorized: </span>
        {row.date} / {row.authorizedDate ?? '—'}
      </span>
      {row.personalFinanceCategory && (
        <span>
          <span className="text-muted-foreground">category: </span>
          {row.personalFinanceCategory}
        </span>
      )}
      <span>
        <span className="text-muted-foreground">would sync: </span>
        {row.wouldSync ? 'yes' : `no — ${row.skipReason ?? 'filtered'}`}
      </span>
    </div>

    {row.dbRawTransactionId ? (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <span>
          <span className="text-muted-foreground">DB row: </span>
          <Link
            to={`/raw-transactions/${row.dbRawTransactionId}`}
            className="text-primary font-mono underline"
          >
            {row.dbRawTransactionId}
          </Link>
        </span>
        <span
          className={cn(
            (row.dbAmount !== row.amount || row.dbCurrency !== row.currency) &&
              'text-amber-600',
          )}
        >
          <span className="text-muted-foreground">DB amount: </span>
          {row.dbAmount != null
            ? formatAmount(row.dbAmount, row.dbCurrency ?? '')
            : '—'}
        </span>
        <span>
          <span className="text-muted-foreground">DB date: </span>
          {row.dbDate ?? '—'}
        </span>
        <span>
          <span className="text-muted-foreground">DB description: </span>
          {row.dbDescription ?? '—'}
        </span>
      </div>
    ) : (
      <div className="text-muted-foreground">
        No RawTransaction with this plaidId in the database.
      </div>
    )}

    <pre className="bg-background border-border max-h-96 overflow-auto rounded border p-2 font-mono">
      {prettyJson(row.plaidJson)}
    </pre>
  </div>
);

const TransactionRow = ({
  row,
  expanded,
  onToggle,
}: {
  row: PreviewRow;
  expanded: boolean;
  onToggle: () => void;
}) => (
  <>
    <tr
      className={cn(
        'border-border hover:bg-accent/30 cursor-pointer border-t',
        row.pending && 'opacity-70',
      )}
      onClick={onToggle}
    >
      <td className="px-3 py-1.5">
        <span className={badgeClass(row.stream === 'added' ? 'blue' : 'purple')}>
          {row.stream}
        </span>
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap">
        {row.authorizedDate ?? row.date}
      </td>
      <td className="max-w-96 truncate px-3 py-1.5">
        <span className="text-foreground">{row.name}</span>
        {row.merchantName && row.merchantName !== row.name && (
          <span className="text-muted-foreground"> · {row.merchantName}</span>
        )}
      </td>
      <td className="text-muted-foreground px-3 py-1.5 whitespace-nowrap">
        {row.accountName ?? (
          <span className="italic">unlinked ({row.plaidAccountId.slice(-6)})</span>
        )}
      </td>
      <td
        className={cn(
          'px-3 py-1.5 text-right font-mono whitespace-nowrap',
          row.amount > 0 ? 'text-positive' : 'text-foreground',
        )}
      >
        {formatAmount(row.amount, row.currency)}
      </td>
      <td className="px-3 py-1.5">
        <DbStatusBadge row={row} />
      </td>
    </tr>
    {expanded && (
      <tr className="border-border border-t">
        <td colSpan={6}>
          <ExpandedRow row={row} />
        </td>
      </tr>
    )}
  </>
);

type StatusFilter = 'all' | 'pending' | 'missing' | 'mismatch' | 'skipped';

const matchesStatusFilter = (row: PreviewRow, filter: StatusFilter): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'pending':
      return row.pending;
    case 'missing':
      return row.dbStatus === 'missing' && !row.pending && row.wouldSync;
    case 'mismatch':
      return row.dbStatus === 'synced' && row.dbMatches === false;
    case 'skipped':
      return !row.wouldSync && !row.pending;
  }
};

export const PlaidDebugView = () => {
  const params = useParams();
  const accessTokenId = params.id || '';
  // Keyed by token id: navigating between two debug URLs reuses the same
  // component instance, and fetched pages from one token must never survive
  // into another's probe (wrong rows, wrong cursor).
  return <PlaidDebugForToken key={accessTokenId} accessTokenId={accessTokenId} />;
};

const PlaidDebugForToken = ({accessTokenId}: {accessTokenId: string}) => {
  const {data: tokensData} = useAllPlaidAccessTokensQuery();
  const token = tokensData?.allPlaidAccessTokens.find(
    (t) => t._id === accessTokenId,
  );

  useBreadcrumb({
    label: token?.institutionName
      ? `Debug · ${token.institutionName}`
      : 'Debug',
    fallback: [
      {label: 'Settings', href: '/settings'},
      {label: 'Connections', href: '/connections'},
    ],
  });

  const [startMode, setStartMode] = React.useState<StartMode>('stored');
  const [customCursor, setCustomCursor] = React.useState('');
  const [countInput, setCountInput] = React.useState('100');
  const [pages, setPages] = React.useState<PreviewPage[]>([]);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [accountFilter, setAccountFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

  // The preview is a live debugging probe — never serve or store cached pages.
  const [runPreview, {loading}] = usePlaidSyncPreviewLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null;

  // The start settings the displayed walk was actually fetched with. When
  // the controls no longer match, "Load next page" is disabled — it would
  // extend the old walk while the controls describe a different one.
  const [walkStartKey, setWalkStartKey] = React.useState<string | null>(null);
  const startKey = (mode: StartMode, cursor: string) =>
    mode === 'custom' ? `custom:${cursor.trim()}` : mode;
  const walkStale =
    lastPage != null && walkStartKey !== startKey(startMode, customCursor);

  const fetchPage = async (reset: boolean) => {
    setFetchError(null);
    const count = Math.min(Math.max(parseInt(countInput, 10) || 100, 1), 500);
    const variables = reset
      ? {
          accessTokenId,
          count,
          ...(startMode === 'start' ? {fromStart: true} : {}),
          // Fetch is disabled while the custom cursor is blank, so this never
          // silently falls back to the stored cursor.
          ...(startMode === 'custom' ? {cursor: customCursor.trim()} : {}),
        }
      : {accessTokenId, count, cursor: lastPage?.nextCursor};
    const result = await runPreview({variables});
    if (result.error) {
      // Plaid invalidates the whole pagination loop when the underlying data
      // mutates mid-walk — the accumulated cursors are dead, so continuing
      // would just retry an invalid cursor. Drop the walk, but pin the next
      // Fetch to the cursor the walk actually started from: re-reading the
      // stored cursor could skip data if the hourly sync advanced it
      // concurrently.
      if (
        !reset &&
        result.error.message.includes(
          'TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION',
        )
      ) {
        const originalCursor = pages[0]?.usedCursor ?? null;
        setPages([]);
        if (originalCursor != null && originalCursor !== '') {
          setStartMode('custom');
          setCustomCursor(originalCursor);
        } else {
          // A null usedCursor means the walk read from the very beginning.
          setStartMode('start');
        }
        setFetchError(
          'Plaid data changed while paginating (TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION); the accumulated pages were discarded. Press Fetch to restart the walk — the original starting cursor has been pre-selected.',
        );
        return;
      }
      // A failed reset fetch must not leave the previous walk's pages on
      // screen: the controls describe the new probe, so stale rows (and
      // their still-loadable next cursor) would masquerade as its results.
      if (reset) {
        setPages([]);
      }
      setFetchError(result.error.message);
      return;
    }
    const page = result.data?.plaidSyncPreview;
    if (page) {
      if (reset) {
        setWalkStartKey(startKey(startMode, customCursor));
        // A new walk starts unfiltered: a selection from the previous walk
        // may name an account the new one lacks — and masking it instead
        // would let it snap back if a later page reintroduced the account.
        setAccountFilter('all');
      }
      setPages((previous) => (reset ? [page] : [...previous, page]));
    }
  };

  const rows: PreviewRow[] = pages.flatMap((page) => [
    ...page.added.map((row) => ({...row, stream: 'added' as const})),
    ...page.modified.map((row) => ({...row, stream: 'modified' as const})),
  ]);

  // Accounts across all fetched pages, newer pages winning.
  const accountsById = new Map<string, PreviewPage['accounts'][number]>();
  for (const page of pages) {
    for (const account of page.accounts) {
      accountsById.set(account.plaidAccountId, account);
    }
  }
  const accounts = [...accountsById.values()];

  const removed = pages.flatMap((page) => page.removed);

  const accountOptions = [
    ...new Map(
      rows.map((row) => [
        row.plaidAccountId,
        row.accountName ?? row.plaidAccountId,
      ]),
    ).entries(),
  ];

  // The filter resets to 'all' on every new walk (see fetchPage), and rows
  // only accumulate within a walk, so a selected option can never vanish.
  const visibleRows = rows.filter(
    (row) =>
      (accountFilter === 'all' || row.plaidAccountId === accountFilter) &&
      matchesStatusFilter(row, statusFilter),
  );

  const pendingCount = rows.filter((row) => row.pending).length;
  const missingCount = rows.filter(
    (row) => row.dbStatus === 'missing' && !row.pending && row.wouldSync,
  ).length;
  const mismatchCount = rows.filter(
    (row) => row.dbStatus === 'synced' && row.dbMatches === false,
  ).length;

  const selectClass =
    'border-input bg-background text-foreground rounded-md border px-2 py-1.5 text-sm';

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* Token summary + fetch controls */}
      <div className="border-border bg-card flex flex-col gap-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground font-medium">
            {token?.institutionName ?? 'Plaid token'}
          </span>
          {token && (
            <>
              <span className="text-muted-foreground font-mono text-sm">
                •••{token.tokenSuffix}
              </span>
              <span
                className={badgeClass(
                  token.environment === 'production' ? 'blue' : 'amber',
                )}
              >
                {token.environment}
              </span>
              <span className={badgeClass(token.active ? 'green' : 'red')}>
                {token.active ? 'active' : 'inactive'}
              </span>
            </>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          Read-only transactionsSync probe: fetches pages directly from Plaid
          (including pending transactions that a real sync filters out) and
          marks how each row compares to the database. The stored sync cursor
          is never modified.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-muted-foreground text-xs">Start from</label>
            <select
              className={selectClass}
              value={startMode}
              // Editing the probe settings mid-flight would relabel results
              // fetched with the old settings — locked while loading, like
              // the other controls below.
              disabled={loading}
              onChange={(e) => {
                if (
                  e.target.value === 'stored' ||
                  e.target.value === 'start' ||
                  e.target.value === 'custom'
                ) {
                  setStartMode(e.target.value);
                }
              }}
            >
              <option value="stored">Stored cursor (DB)</option>
              <option value="start">Beginning of history</option>
              <option value="custom">Custom cursor</option>
            </select>
          </div>
          {startMode === 'custom' && (
            <div className="flex min-w-64 flex-1 flex-col gap-1">
              <label className="text-muted-foreground text-xs">Cursor</label>
              <input
                type="text"
                className={cn(selectClass, 'font-mono')}
                value={customCursor}
                disabled={loading}
                onChange={(e) => setCustomCursor(e.target.value)}
                placeholder="Paste a cursor…"
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-muted-foreground text-xs">Page size</label>
            <input
              type="number"
              min={1}
              max={500}
              className={cn(selectClass, 'w-24')}
              value={countInput}
              disabled={loading}
              onChange={(e) => setCountInput(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            disabled={
              loading ||
              !accessTokenId ||
              (startMode === 'custom' && customCursor.trim() === '')
            }
            onClick={() => {
              void fetchPage(true);
            }}
          >
            {loading ? 'Fetching…' : 'Fetch'}
          </Button>
          {lastPage && (
            <Button
              variant="outline"
              disabled={loading || !lastPage.hasMore || walkStale}
              onClick={() => {
                void fetchPage(false);
              }}
            >
              {lastPage.hasMore ? 'Load next page' : 'No more pages'}
            </Button>
          )}
          {walkStale && (
            <span className="text-muted-foreground self-center text-xs">
              Start settings changed — press Fetch to begin a new walk.
            </span>
          )}
        </div>
        {fetchError && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {fetchError}
          </div>
        )}
        {lastPage && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <CursorChip
              label={`Used cursor (${lastPage.cursorSource}):`}
              cursor={lastPage.usedCursor ?? null}
            />
            <CursorChip label="Next cursor:" cursor={lastPage.nextCursor} />
            <span className="text-muted-foreground text-xs">
              {pages.length} page{pages.length === 1 ? '' : 's'} ·{' '}
              {rows.length} rows · {removed.length} removed ·{' '}
              {lastPage.hasMore ? 'more available' : 'end of updates'}
            </span>
          </div>
        )}
      </div>

      {loading && pages.length === 0 && <Loading size="medium" />}

      {/* Accounts on the item, as reported by this sync page */}
      {accounts.length > 0 && (
        <div className="border-border bg-card flex flex-col gap-2 rounded-lg border p-4">
          <span className="text-foreground text-sm font-medium">
            Accounts in response
          </span>
          <div className="flex flex-wrap gap-2">
            {accounts.map((account) => (
              <span
                key={account.plaidAccountId}
                className="border-border flex items-center gap-2 rounded border px-2 py-1 text-xs"
              >
                <span className="text-foreground">
                  {account.name}
                  {account.mask ? ` ••${account.mask}` : ''}
                </span>
                <span className="text-muted-foreground">
                  {account.type}
                  {account.subtype ? `/${account.subtype}` : ''}
                </span>
                {account.currentBalance != null && (
                  <span className="font-mono">
                    {formatAmount(
                      account.currentBalance,
                      account.currency ?? '',
                    )}
                  </span>
                )}
                {account.accountName ? (
                  <span className={badgeClass('green')}>
                    → {account.accountName}
                  </span>
                ) : (
                  <span className={badgeClass('red')}>not linked</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Transactions */}
      {pages.length > 0 && (
        <div className="border-border bg-card flex flex-col rounded-lg border">
          <div className="flex flex-wrap items-center gap-3 p-3">
            <span className="text-foreground text-sm font-medium">
              Transactions ({visibleRows.length}
              {visibleRows.length !== rows.length ? ` of ${rows.length}` : ''})
            </span>
            <span className="text-muted-foreground text-xs">
              {pendingCount} pending · {missingCount} not in DB ·{' '}
              {mismatchCount} differ
            </span>
            <div className="ml-auto flex items-center gap-2">
              <select
                className={selectClass}
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
              >
                <option value="all">All accounts</option>
                {accountOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className={selectClass}
                value={statusFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === 'all' ||
                    value === 'pending' ||
                    value === 'missing' ||
                    value === 'mismatch' ||
                    value === 'skipped'
                  ) {
                    setStatusFilter(value);
                  }
                }}
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending only</option>
                <option value="missing">Not in DB</option>
                <option value="mismatch">In DB but differs</option>
                <option value="skipped">Would be skipped</option>
              </select>
            </div>
          </div>
          {visibleRows.length === 0 ? (
            <div className="text-muted-foreground p-6 text-center text-sm">
              No transactions in the fetched pages
              {rows.length > 0 ? ' match the filters' : ''}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs">
                    <th className="px-3 py-2 font-medium">Stream</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 font-medium">Account</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">DB status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <TransactionRow
                      key={`${row.stream}-${row.transactionId}`}
                      row={row}
                      expanded={expandedId === row.transactionId}
                      onToggle={() =>
                        setExpandedId(
                          expandedId === row.transactionId
                            ? null
                            : row.transactionId,
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Removed transactions */}
      {removed.length > 0 && (
        <div className="border-border bg-card flex flex-col gap-2 rounded-lg border p-4">
          <span className="text-foreground text-sm font-medium">
            Removed by Plaid ({removed.length})
          </span>
          <div className="flex flex-col gap-1">
            {removed.map((entry) => (
              <div
                key={entry.transactionId}
                className="flex items-center gap-2 text-xs"
              >
                <span className="font-mono">{entry.transactionId}</span>
                {entry.dbRawTransactionId ? (
                  <Link
                    to={`/raw-transactions/${entry.dbRawTransactionId}`}
                    className={cn(
                      badgeClass(
                        entry.dbStatus === 'deleted' ? 'slate' : 'amber',
                      ),
                      'underline',
                    )}
                  >
                    {entry.dbStatus === 'deleted'
                      ? 'already deleted in DB'
                      : 'still present in DB'}
                  </Link>
                ) : (
                  <span className={badgeClass('green')}>not in DB</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
